import React, { useState, useEffect } from 'react';
import { apiLogsAPI } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Refresh,
  FilterList,
  Clear,
  Visibility,
  Download,
  TrendingUp,
  TrendingDown,
  Speed,
  Error as ErrorIcon,
  CheckCircle,
  Warning
} from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

// Define API log type
interface ApiLog {
  id: string;
  timestamp: string;
  method: string;
  endpoint: string;
  statusCode: number;
  responseTime: number;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
  requestBody?: string;
  errorMessage?: string;
  
  // Enhanced fields
  requestId?: string;
  contentType?: string;
  requestSize?: number;
  referer?: string;
  origin?: string;
  clientIp?: string;
  sessionId?: string;
  
  // Headers
  headers?: Record<string, string>;
  allHeaders?: Record<string, string[]>;
  
  // Query parameters
  queryParams?: string;
  requestPath?: string;
}

// Define statistics type
interface ApiStats {
  totalRequests: number;
  averageResponseTime: number;
  successRate: number;
  topEndpoints: Array<{
    endpoint: string;
    count: number;
    averageResponseTime: number;
  }>;
  statusCodeDistribution: Array<{
    statusCode: number;
    count: number;
    percentage: number;
  }>;
  
  // Enhanced metrics
  totalRequestSize: number;
  // Note: response size metrics removed to prevent response corruption
  averageRequestSize: number;
  uniqueUsers: number;
  uniqueIPs: number;
  requestsWithQueryParams: number;
  topUserAgents: Array<{
    userAgent: string;
    count: number;
  }>;
  topContentTypes: Array<{
    contentType: string;
    count: number;
  }>;
}

const ApiLogs: React.FC = () => {
  const [logs, setLogs] = useState<ApiLog[]>([]);
  const [statistics, setStatistics] = useState<ApiStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedLog, setSelectedLog] = useState<ApiLog | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filterMethod, setFilterMethod] = useState<string>('');
  const [filterStatusCode, setFilterStatusCode] = useState<string>('');
  const [filterRequestId, setFilterRequestId] = useState<string>('');
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterQueryParams, setFilterQueryParams] = useState<string>('');
  const [filterEndpoint, setFilterEndpoint] = useState<string>('');

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
  }, [page, rowsPerPage]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiLogsAPI.getLatestLogs();
      setLogs(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error fetching logs:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const res = await apiLogsAPI.getLogsWithPagination(0, 1000);
      const allLogs = res.data.content || [];
      
      // Calculate statistics from logs
      const totalRequests = allLogs.length;
      const successfulRequests = allLogs.filter((log: ApiLog) => log.statusCode < 400).length;
      const successRate = totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0;
      
      const averageResponseTime = allLogs.length > 0 
        ? allLogs.reduce((sum: number, log: ApiLog) => sum + (log.responseTime || 0), 0) / allLogs.length 
        : 0;

      // Group by endpoint
      const endpointStats = allLogs.reduce((acc: Record<string, { count: number; totalTime: number }>, log: ApiLog) => {
        if (!acc[log.endpoint]) {
          acc[log.endpoint] = { count: 0, totalTime: 0 };
        }
        acc[log.endpoint].count++;
        acc[log.endpoint].totalTime += log.responseTime || 0;
        return acc;
      }, {});

      const topEndpoints = Object.entries(endpointStats)
        .map(([endpoint, stats]) => ({
          endpoint,
          count: (stats as { count: number; totalTime: number }).count,
          averageResponseTime: (stats as { count: number; totalTime: number }).totalTime / (stats as { count: number; totalTime: number }).count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Status code distribution
      const statusCodeCounts = allLogs.reduce((acc: Record<number, number>, log: ApiLog) => {
        acc[log.statusCode] = (acc[log.statusCode] || 0) + 1;
        return acc;
      }, {});

      const statusCodeDistribution = Object.entries(statusCodeCounts)
        .map(([statusCode, count]) => ({
          statusCode: parseInt(statusCode),
          count: count as number,
          percentage: (count as number / totalRequests) * 100
        }))
        .sort((a, b) => b.count - a.count);

      // Calculate enhanced metrics
      const totalRequestSize = allLogs.reduce((sum: number, log: ApiLog) => sum + (log.requestSize || 0), 0);
      const averageRequestSize = allLogs.length > 0 ? totalRequestSize / allLogs.length : 0;
      
      // Unique users and IPs
      const uniqueUsers = new Set(allLogs.filter((log: ApiLog) => log.userId).map((log: ApiLog) => log.userId)).size;
      const uniqueIPs = new Set(allLogs.filter((log: ApiLog) => log.clientIp || log.ipAddress).map((log: ApiLog) => log.clientIp || log.ipAddress)).size;
      
      // Requests with query parameters
      const requestsWithQueryParams = allLogs.filter((log: ApiLog) => log.queryParams && log.queryParams.trim().length > 0).length;
      
      // Top user agents
      const userAgentCounts = allLogs.reduce((acc: Record<string, number>, log: ApiLog) => {
        if (log.userAgent) {
          acc[log.userAgent] = (acc[log.userAgent] || 0) + 1;
        }
        return acc;
      }, {});
      const topUserAgents = Object.entries(userAgentCounts)
        .map(([userAgent, count]) => ({ userAgent, count: count as number }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      // Top content types
      const contentTypeCounts = allLogs.reduce((acc: Record<string, number>, log: ApiLog) => {
        if (log.contentType) {
          acc[log.contentType] = (acc[log.contentType] || 0) + 1;
        }
        return acc;
      }, {});
      const topContentTypes = Object.entries(contentTypeCounts)
        .map(([contentType, count]) => ({ contentType, count: count as number }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setStatistics({
        totalRequests,
        averageResponseTime,
        successRate,
        topEndpoints,
        statusCodeDistribution,
        totalRequestSize,
        averageRequestSize,
        uniqueUsers,
        uniqueIPs,
        requestsWithQueryParams,
        topUserAgents,
        topContentTypes
      });
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Error fetching statistics:', error);
    }
  };

  const handleRefresh = () => {
    fetchLogs();
    fetchStatistics();
  };

  const handleClearFilters = () => {
    setFilterMethod('');
    setFilterStatusCode('');
    setFilterRequestId('');
    setFilterUserId('');
    setFilterQueryParams('');
    setFilterEndpoint('');
  };

  const handleClearLogs = async () => {
    if (window.confirm('Are you sure you want to clear all API logs?')) {
      try {
        await apiLogsAPI.clearAllLogs();
        fetchLogs();
        fetchStatistics();
      } catch (err) {
        setError('Failed to clear logs');
      }
    }
  };

  const getMethodColor = (method: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'primary';
      case 'POST':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'secondary';
    }
  };

  const getStatusColor = (statusCode: number): 'success' | 'warning' | 'error' => {
    if (statusCode < 300) return 'success';
    if (statusCode < 400) return 'warning';
    return 'error';
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatResponseTime = (time: number) => {
    return `${time}ms`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatQueryParams = (queryParams: string) => {
    if (!queryParams || queryParams.trim().length === 0) return '-';
    
    // Try to parse and format the query string nicely
    try {
      const params = new URLSearchParams(queryParams);
      const formattedParams: string[] = [];
      
      params.forEach((value, key) => {
        if (value && value.trim().length > 0) {
          formattedParams.push(`${key}=${value.length > 20 ? value.substring(0, 20) + '...' : value}`);
        } else {
          formattedParams.push(key);
        }
      });
      
      return formattedParams.join('&');
    } catch (error) {
      // If parsing fails, return the original string truncated
      return queryParams.length > 30 ? queryParams.substring(0, 30) + '...' : queryParams;
    }
  };

  const handleViewDetails = (log: ApiLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const filteredLogs = logs.filter(log => {
    if (filterMethod && log.method !== filterMethod) return false;
    if (filterStatusCode && log.statusCode.toString() !== filterStatusCode) return false;
    if (filterRequestId && log.requestId && !log.requestId.toLowerCase().includes(filterRequestId.toLowerCase())) return false;
    if (filterUserId && log.userId && !log.userId.toLowerCase().includes(filterUserId.toLowerCase())) return false;
    if (filterQueryParams && log.queryParams && !log.queryParams.toLowerCase().includes(filterQueryParams.toLowerCase())) return false;
    if (filterEndpoint && log.endpoint && !log.endpoint.toLowerCase().includes(filterEndpoint.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        API Logs
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Requests
                </Typography>
                <Typography variant="h4">
                  {statistics.totalRequests}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Success Rate
                </Typography>
                <Typography variant="h4">
                  {statistics.totalRequests > 0 
                    ? Math.round(statistics.successRate)
                    : 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Errors
                </Typography>
                <Typography variant="h4" color="error">
                  {statistics.totalRequests - statistics.successRate}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Methods
                </Typography>
                <Typography variant="body2">
                  GET: {statistics.topEndpoints.find(e => e.endpoint.includes('GET'))?.count || 0} | POST: {statistics.topEndpoints.find(e => e.endpoint.includes('POST'))?.count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* Enhanced Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Unique Users
                </Typography>
                <Typography variant="h4">
                  {statistics.uniqueUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Unique IPs
                </Typography>
                <Typography variant="h4">
                  {statistics.uniqueIPs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Request Size
                </Typography>
                <Typography variant="h4">
                  {formatFileSize(statistics.averageRequestSize)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  With Query Params
                </Typography>
                <Typography variant="h4">
                  {statistics.requestsWithQueryParams}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {statistics.totalRequests > 0 ? Math.round((statistics.requestsWithQueryParams / statistics.totalRequests) * 100) : 0}% of total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Method</InputLabel>
              <Select
                value={filterMethod}
                onChange={(e: SelectChangeEvent<string>) => setFilterMethod(e.target.value)}
                label="Method"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Code</InputLabel>
              <Select
                value={filterStatusCode}
                onChange={(e: SelectChangeEvent<string>) => setFilterStatusCode(e.target.value)}
                label="Status Code"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="200">200 (Success)</MenuItem>
                <MenuItem value="404">404 (Not Found)</MenuItem>
                <MenuItem value="500">500 (Server Error)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Request ID"
              value={filterRequestId}
              onChange={(e) => setFilterRequestId(e.target.value)}
              placeholder="Search by request ID"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="User ID"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              placeholder="Search by user ID"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Query Parameters"
              value={filterQueryParams}
              onChange={(e) => setFilterQueryParams(e.target.value)}
              placeholder="Search in query params"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Endpoint"
              value={filterEndpoint}
              onChange={(e) => setFilterEndpoint(e.target.value)}
              placeholder="Search by endpoint"
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Clear />}
                onClick={handleClearLogs}
              >
                Clear All
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Logs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Response Time</TableCell>
                <TableCell>Request ID</TableCell>
                <TableCell>Client IP</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Request Size</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.method} 
                      color={getMethodColor(log.method)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {log.endpoint}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={log.statusCode} 
                      color={getStatusColor(log.statusCode)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatResponseTime(log.responseTime)}
                      color={log.responseTime > 1000 ? 'error' : 'default'}
                      size="small"
                      icon={log.responseTime > 1000 ? <Speed /> : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {log.requestId ? log.requestId.substring(0, 8) + '...' : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{log.clientIp || log.ipAddress || '-'}</TableCell>
                  <TableCell>{log.userId || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {log.requestSize ? formatFileSize(log.requestSize) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(log)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Removed TablePagination as count is -1 */}
      </Paper>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>API Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Request Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography><strong>Method:</strong> {selectedLog.method}</Typography>
                  <Typography><strong>Endpoint:</strong> {selectedLog.endpoint}</Typography>
                  <Typography><strong>Status Code:</strong> {selectedLog.statusCode}</Typography>
                  <Typography><strong>Response Time:</strong> {formatResponseTime(selectedLog.responseTime)}</Typography>
                  <Typography><strong>Request ID:</strong> {selectedLog.requestId || 'N/A'}</Typography>
                  <Typography><strong>Content Type:</strong> {selectedLog.contentType || 'N/A'}</Typography>
                  <Typography><strong>Request Path:</strong> {selectedLog.requestPath || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography><strong>Client IP:</strong> {selectedLog.clientIp || selectedLog.ipAddress || 'N/A'}</Typography>
                  <Typography><strong>User ID:</strong> {selectedLog.userId || 'N/A'}</Typography>
                  <Typography><strong>User Agent:</strong> {selectedLog.userAgent || 'N/A'}</Typography>
                  <Typography><strong>Request Size:</strong> {selectedLog.requestSize ? formatFileSize(selectedLog.requestSize) : 'N/A'}</Typography>
                  <Typography><strong>Session ID:</strong> {selectedLog.sessionId || 'N/A'}</Typography>
                </Grid>
              </Grid>
              
              {selectedLog.queryParams && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Query Parameters
                  </Typography>
                  <Box sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {selectedLog.queryParams}
                    </Typography>
                  </Box>
                </>
              )}
              
              {selectedLog.referer && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Referer & Origin
                  </Typography>
                  <Typography><strong>Referer:</strong> {selectedLog.referer}</Typography>
                  <Typography><strong>Origin:</strong> {selectedLog.origin || 'N/A'}</Typography>
                </>
              )}
              
              {selectedLog.allHeaders && Object.keys(selectedLog.allHeaders).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Request Headers ({Object.keys(selectedLog.allHeaders).length})
                  </Typography>
                  <Box sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                    {Object.entries(selectedLog.allHeaders).map(([key, values]) => (
                      <Box key={key} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#666' }}>
                          {key}:
                        </Typography>
                        {Array.isArray(values) ? values.map((value, index) => (
                          <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', ml: 2 }}>
                            {value}
                          </Typography>
                        )) : (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', ml: 2 }}>
                            {values}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              
              {selectedLog.requestBody && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Request Body
                  </Typography>
                  <Box sx={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {selectedLog.requestBody}
                    </Typography>
                  </Box>
                </>
              )}



              {selectedLog.errorMessage && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Error Message
                  </Typography>
                  <Typography variant="body2" color="error" sx={{ backgroundColor: '#ffebee', p: 1, borderRadius: 1 }}>
                    {selectedLog.errorMessage}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiLogs; 